import { Box, Link, Typography } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { APP_LINK_STATE } from '../../Home/navRoutes';
import { COLORS } from '../../IndividualPlan/tokens';
import { CountLink } from '../components/CountLink';
import { CELL_SX, FIRST_CELL_SX, HEAD_ROW_SX, ROW_SX, TABLE_SX, growthCell, levelCell, scoredSample } from '../components/GradeTable';
import { SmallGroupMarker } from '../components/SmallGroupMarker';
import { GRADE_UNKNOWN, describeListKey, gradeHref, gradeName, schoolComponentOf, schoolHref } from '../listKeys';
import type { PeCohortDetail, PeCohortSchoolRow, PeGradeDetail, ScoreUnit } from '../types';
import { EM_DASH, fmtGrowthIn, levelNoun, pct } from '../utils/drilldownFormat';
import {
  DetailCard,
  DetailStrip,
  DrilldownFrame,
  DrilldownPage,
  NotFoundPage,
  StudentsLinkCard,
  sameDataSentence,
  type LoadedReport,
} from './DrilldownFrame';

const LINK_SX = { color: COLORS.link, fontWeight: 600 } as const;

function narrativeFor(cohort: PeCohortDetail, resetYear: boolean, unit: ScoreUnit, scoresAbsent: boolean): string | undefined {
  if (scoresAbsent || cohort.small || cohort.count === 0 || scoredSample(cohort, unit) === 0) return undefined;
  if (resetYear) {
    return `${cohort.label} are on the new ACCESS scale this year; growth against last year returns with the Spring 2027 results.`;
  }
  const growthOf = (g: { growth: number | null; growth_scale: number | null }) => (unit === 'scale_score' ? g.growth_scale : g.growth);
  const lastOf = (g: { last_growth: number | null; last_growth_scale: number | null }) => (unit === 'scale_score' ? g.last_growth_scale : g.last_growth);
  const top = [...cohort.by_grade]
    .filter((g) => g.count > 0 && !g.small && growthOf(g) != null)
    .sort((a, b) => (growthOf(b) ?? 0) - (growthOf(a) ?? 0))[0];
  if (!top) return undefined;
  const against =
    lastOf(top) != null
      ? `against ${fmtGrowthIn(unit, top.last_growth, top.last_growth_scale)} for the same group last year`
      : 'with no comparable group last year';
  return `${cohort.label} in ${gradeName(top.grade, top.grade_label).toLowerCase()} grew ${fmtGrowthIn(unit, top.growth, top.growth_scale)}, ${against}.`;
}

type SchoolGroup = { kind: string; rows: PeCohortSchoolRow[] };

function schoolGroups(rows: PeCohortSchoolRow[]): SchoolGroup[] {
  const order = ['Elementary', 'Middle', 'High'];
  const kinds = [...order.filter((k) => rows.some((r) => r.kind === k)), ...rows.map((r) => r.kind).filter((k) => !order.includes(k))];
  return [...new Set(kinds)].map((kind) => ({ kind, rows: rows.filter((r) => r.kind === kind) }));
}

function CohortBody({ loaded, cohort }: { loaded: LoadedReport; cohort: PeCohortDetail }) {
  const { resetYear, settings, generatedOn, unit, drilldown, report } = loaded;
  const scoresAbsent = report.assessment_data_absent;
  const threshold = settings.smallGroupThreshold;
  const tenureRule = cohort.cohort === 'ltel';
  const longTermYears = settings.ltelMinYears - 1;
  const belowTenure = (grade: number) => tenureRule && grade !== GRADE_UNKNOWN && grade <= longTermYears - 2;
  const holds = (g: { count: number; met: number }) => g.count > 0 || g.met > 0;
  const unreachable = (g: { grade: number; count: number; met: number }) => belowTenure(g.grade) && !holds(g);
  const heldBy = new Set(cohort.by_grade.filter(holds).map((g) => g.grade));
  const missing = [...drilldown.grades].filter((g) => belowTenure(g.grade) && !heldBy.has(g.grade)).sort((a, b) => a.grade - b.grade);
  const bands: { from: PeGradeDetail; to: PeGradeDetail }[] = [];
  for (const g of missing) {
    const last = bands[bands.length - 1];
    if (last && g.grade === last.to.grade + 1) last.to = g;
    else bands.push({ from: g, to: g });
  }
  const gradeRows = cohort.by_grade.filter((g) => !unreachable(g));
  const rank = (grade: number) => (grade === GRADE_UNKNOWN ? Number.POSITIVE_INFINITY : grade);
  const rowsWithBands = [...bands.map((b) => ({ kind: 'band' as const, band: b, grade: b.from.grade })), ...gradeRows.map((g) => ({ kind: 'row' as const, row: g, grade: g.grade }))].sort((a, b) => rank(a.grade) - rank(b.grade));
  const lower = cohort.label.toLowerCase();
  const awaiting = cohort.last_count == null ? (cohort.awaiting_confirmation ?? 0) : 0;

  return (
    <DrilldownPage
      crumbs={[{ label: cohort.label }]}
      title={cohort.label}
      subtitle={`${cohort.definition}. ${sameDataSentence(generatedOn)}`}
    >
      <DetailStrip
        items={[
          { label: 'Students', value: <CountLink count={cohort.count} listKey={cohort.key} /> },
          { label: 'Share of active ELs', value: `${Math.round(cohort.share * 100)}%` },
          { label: `Composite ${levelNoun(unit)}`, value: levelCell(cohort.level, cohort.level_scale, unit) },
          { label: 'Met exit criteria', value: scoresAbsent ? EM_DASH : <CountLink count={cohort.met} listKey={`cohort:${cohort.cohort}:met`} /> },
          { label: 'Last year', value: cohort.last_count == null ? EM_DASH : String(cohort.last_count) },
        ]}
      />
      {awaiting > 0 && (
        <Typography component="p" sx={{ m: 0, fontSize: 14, lineHeight: '20px', color: COLORS.muted }}>
          {`${awaiting} students await newcomer confirmation before this can be compared with last year`}
        </Typography>
      )}

      <DetailCard
        title="By grade"
        description={`${cohort.label} in each grade, against the ${lower} in the same grade last year. Open a grade to see it across the district.`}
        narrative={narrativeFor(cohort, resetYear, unit, scoresAbsent)}
      >
        <Box data-testid="by-grade-table" sx={TABLE_SX}>
          <Box sx={HEAD_ROW_SX}>
            <Box sx={{ ...CELL_SX, width: 212 }}>Grade</Box>
            <Box sx={{ ...CELL_SX, width: 140 }}>{cohort.label}</Box>
            <Box sx={{ ...CELL_SX, width: 140 }}>{`Composite ${levelNoun(unit)}`}</Box>
            <Box sx={CELL_SX}>Growth this year</Box>
            <Box sx={{ ...CELL_SX, width: 170 }}>Same grade last year</Box>
          </Box>
          {rowsWithBands.map((item) =>
            item.kind === 'band' ? (
              <Box key={`band:${item.band.from.grade}`} sx={ROW_SX}>
                <Box sx={{ ...CELL_SX, width: 212 }}>
                  {`${gradeName(item.band.from.grade, item.band.from.grade_label)}–${gradeName(item.band.to.grade, item.band.to.grade_label)}`}
                </Box>
                <Box sx={{ flex: 1, color: COLORS.muted }}>
                  {`n/a · a student can’t reach ${longTermYears + 1} years in the program before ${gradeName(longTermYears - 1).toLowerCase()}`}
                </Box>
              </Box>
            ) : (
              (() => {
                const g = item.row;
                return (
            <Box key={g.key} sx={ROW_SX}>
                  <Box sx={{ ...CELL_SX, width: 212 }}>
                    <Link component={RouterLink} to={gradeHref(g.grade)} state={APP_LINK_STATE} underline="hover" sx={LINK_SX}>
                      {gradeName(g.grade, g.grade_label)}
                    </Link>
                  </Box>
                  <Box sx={{ ...CELL_SX, width: 140 }}>
                    <CountLink count={g.count} listKey={g.key} />
                    {g.small && g.count > 0 && <SmallGroupMarker threshold={threshold} />}
                  </Box>
                  <Box sx={{ ...CELL_SX, width: 140 }}>{levelCell(g.level, g.level_scale, unit)}</Box>
                  <Box sx={CELL_SX}>{g.count === 0 || scoresAbsent ? EM_DASH : growthCell(g.growth, g.growth_scale, unit, resetYear, scoredSample(g, unit))}</Box>
                  <Box sx={{ ...CELL_SX, width: 170, color: COLORS.muted }}>
                    {g.count === 0 || scoresAbsent ? EM_DASH : growthCell(g.last_growth, g.last_growth_scale, unit, resetYear, 0)}
                  </Box>
                </Box>
    
                );
              })()
            ),
          )}
        </Box>
      </DetailCard>

      {cohort.by_years && (
        <DetailCard title="By years in the program">
          <Box data-testid="by-years" sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {cohort.by_years.map((y) => (
              <Box key={y.label} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 96 }}>
                <Box component="span" sx={{ fontSize: 12, fontWeight: 700, color: COLORS.muted }}>
                  {y.label}
                </Box>
                <Box component="span" sx={{ fontSize: 20, fontWeight: 700, color: COLORS.ink }}>
                  {y.count}
                </Box>
              </Box>
            ))}
          </Box>
        </DetailCard>
      )}

      <DetailCard title="By school" description={`Counts only. Open a school to see its ${lower} grade by grade.`}>
        <Box data-testid="by-school-table" sx={TABLE_SX}>
          <Box sx={HEAD_ROW_SX}>
            <Box sx={FIRST_CELL_SX}>School</Box>
            <Box sx={{ ...CELL_SX, width: 160 }}>{cohort.label}</Box>
            <Box sx={{ ...CELL_SX, width: 200 }}>Share of school’s ELs</Box>
            <Box sx={{ ...CELL_SX, width: 160 }}>Met exit criteria</Box>
          </Box>
          {schoolGroups(cohort.by_school).map(({ kind, rows }) => {
            if (kind === 'Elementary') {
              const n = rows.reduce((sum, r) => sum + r.count, 0);
              const of = rows.reduce((sum, r) => sum + r.of, 0);
              const met = rows.reduce((sum, r) => sum + r.met, 0);
              return (
                <Box key={kind} sx={{ ...ROW_SX, color: COLORS.muted, fontWeight: 700 }}>
                  <Box sx={FIRST_CELL_SX}>{`All elementary · ${rows.length} school${rows.length === 1 ? '' : 's'}`}</Box>
                  <Box sx={{ ...CELL_SX, width: 160 }}>{n}</Box>
                  <Box sx={{ ...CELL_SX, width: 200 }}>{pct(n, of)}</Box>
                  <Box sx={{ ...CELL_SX, width: 160 }}>{scoresAbsent ? EM_DASH : met}</Box>
                </Box>
              );
            }
            return rows.map((r) => (
              <Box key={r.key} sx={ROW_SX}>
                <Box sx={FIRST_CELL_SX}>
                  <Link component={RouterLink} to={schoolHref(schoolComponentOf(r.key))} state={APP_LINK_STATE} underline="hover" sx={LINK_SX}>
                    {r.school}
                  </Link>
                </Box>
                <Box sx={{ ...CELL_SX, width: 160 }}>
                  <CountLink count={r.count} listKey={r.key} />
                  {r.small && r.count > 0 && <SmallGroupMarker threshold={threshold} />}
                </Box>
                <Box sx={{ ...CELL_SX, width: 200 }}>{pct(r.count, r.of)}</Box>
                <Box sx={{ ...CELL_SX, width: 160 }}>{scoresAbsent ? EM_DASH : <CountLink count={r.met} listKey={`${r.key}:met`} />}</Box>
              </Box>
            ));
          })}
        </Box>
      </DetailCard>

      <StudentsLinkCard count={cohort.count} listKey={cohort.key} noun="in this cohort" />
    </DrilldownPage>
  );
}

export function CohortDetailPage() {
  const { cohort: param = '' } = useParams<{ cohort: string }>();
  const crumbs = [{ label: describeListKey(`cohort:${param}`).title }];
  return (
    <DrilldownFrame crumbs={crumbs}>
      {(loaded) => {
        const cohort = loaded.drilldown.cohorts.find((c) => c.cohort === param);
        if (!cohort) {
          return <NotFoundPage crumbs={crumbs} loaded={loaded} message="That cohort isn’t in this report." />;
        }
        return <CohortBody loaded={loaded} cohort={cohort} />;
      }}
    </DrilldownFrame>
  );
}
